// Phase 9.1.B — Client portal auth state.
//
// Mirrors apps/web/src/auth/AuthProvider.tsx but targets /portal/auth/*
// endpoints. PortalUser carries `clientId` so dashboard / order-history
// queries can scope by it without an extra round-trip.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, bindAuth } from "@/lib/api";
import { makeSessionRefresher, type RefreshOutcome } from "@/lib/session-refresh";

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
  client_id: string;
}

interface AuthState {
  user: PortalUser | null;
  /** Page load couldn't reach the server (deploy / outage) and keeps
   *  retrying — RequireAuth shows "Can't reach … — retrying…", never /login. */
  unreachable: boolean;
  /** The Retry button: try again now instead of after the current wait. */
  retryNow: () => void;
  /** Gave up retrying (MAX_UNREACHABLE_CYCLES rounds, or the server said
   *  something retrying won't fix): show `unreachableMessage` + "Sign in
   *  again" instead of retrying forever. */
  unreachableGaveUp: boolean;
  unreachableMessage: string | null;
  /** True until we've finished the initial /portal/auth/refresh attempt. */
  loading: boolean;
  /** Resolves with who signed in, so the caller can pick their home page. */
  login: (email: string, password: string) => Promise<PortalUser>;
  logout: () => Promise<void>;
  /** Called by Signup + VerifyEmail flows when they receive an
   *  access_token + user payload directly (skips a /portal/auth/me roundtrip). */
  setSession: (accessToken: string, user: PortalUser) => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Rounds of renewal retries on page load before offering "Sign in again". */
const MAX_UNREACHABLE_CYCLES = 3;

/** One renewer per page: every caller shares the same in-flight renewal. */
const renewSession = makeSessionRefresher<PortalUser>({ url: "/portal/auth/refresh" });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const [unreachableGaveUp, setUnreachableGaveUp] = useState(false);
  const [unreachableMessage, setUnreachableMessage] = useState<string | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const navigate = useNavigate();

  // A fresh sign-in or sign-out ends any "can't reach" state from page load.
  const clearUnreachable = useCallback(() => {
    setUnreachable(false);
    setUnreachableGaveUp(false);
    setUnreachableMessage(null);
  }, []);

  const handleLogout = useCallback(() => {
    clearUnreachable();
    accessTokenRef.current = null;
    setUser(null);
    navigate("/login");
  }, [navigate, clearUnreachable]);

  // Renew from the refresh cookie: single flight, retried through deploys,
  // "signed out" only on a definitive 401 (lib/session-refresh.ts).
  const refresh = useCallback(async (): Promise<RefreshOutcome<PortalUser>> => {
    const outcome = await renewSession();
    if (outcome.kind === "ok") accessTokenRef.current = outcome.accessToken;
    return outcome;
  }, []);

  useEffect(() => {
    bindAuth({
      getAccessToken: () => accessTokenRef.current,
      refreshToken: refresh,
      onLogout: handleLogout,
    });
  }, [refresh, handleLogout]);

  // On first mount: silent refresh + load /portal/auth/me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Keep trying until the server gives a clear answer. A deploy or an
      // outage shows "Can't reach … — retrying…" (RequireAuth) instead of
      // the sign-in page; the cookie is kept throughout.
      let outcome = await refresh();
      let cycles = 0;
      while (!cancelled && outcome.kind === "unavailable") {
        cycles++;
        setUnreachable(true);
        setUnreachableMessage(outcome.message ?? null);
        // A way out: after a few rounds (~1 min each), or at once when the
        // server says something retrying won't fix, offer "Sign in again".
        if (outcome.definitive || cycles >= MAX_UNREACHABLE_CYCLES) {
          setUnreachableGaveUp(true);
          setLoading(false);
          return;
        }
        outcome = await refresh();
      }
      if (cancelled) return;
      setUnreachable(false);
      if (outcome.kind !== "ok") {
        setLoading(false); // definitively signed out → the sign-in page
        return;
      }
      try {
        // The renewal already says who this is; /me only as a fallback.
        const sessionUser =
          outcome.user ?? (await api<{ user: PortalUser }>("/portal/auth/me")).user;
        if (!cancelled) setUser(sessionUser);
      } catch {
        accessTokenRef.current = null;
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const body = await api<{
        access_token: string;
        user: PortalUser;
      }>("/portal/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
        skipAuthRefresh: true,
        skipAuth: true,
      });
      clearUnreachable();
      accessTokenRef.current = body.access_token;
      setUser(body.user);
      return body.user;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api("/portal/auth/logout", { method: "POST" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // already gone
      } else {
        console.warn("[portal-auth/logout] server call failed", err);
      }
    } finally {
      handleLogout();
    }
  }, [handleLogout]);

  const setSession = useCallback((accessToken: string, u: PortalUser) => {
    clearUnreachable();
    accessTokenRef.current = accessToken;
    setUser(u);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, unreachable, unreachableGaveUp, unreachableMessage, retryNow: renewSession.retryNow, login, logout, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
